import React, { useRef, useEffect } from 'react';
import './AutoResizingTextArea.scss';

export type AutoResizingTextareaProps = {
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  disabled?: boolean;
  maxHeight?: number; // Add maxHeight prop
};

export default function AutoResizingTextarea({
  value,
  onChange,
  placeholder,
  onKeyDown,
  disabled,
  maxHeight = 120 // Default max height
}: AutoResizingTextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // This effect adjusts the textarea height after each render
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // For empty content, use fixed minimum height
      if (!value || value.trim() === '') {
        textarea.style.height = '42px';
        textarea.style.overflowY = 'hidden';
        return;
      }

      // For content, measure what's needed
      textarea.style.height = 'auto';
      const scrollHeight = textarea.scrollHeight;

      // Only grow beyond minimum if content truly needs it
      // Check if content requires more than one line
      const singleLineHeight = 42; // Our minimum height

      if (scrollHeight <= singleLineHeight + 4) {
        // Content fits in single line (with small buffer)
        textarea.style.height = '42px';
        textarea.style.overflowY = 'hidden';
      } else {
        // Content needs multiple lines
        const newHeight = Math.min(scrollHeight, maxHeight);
        textarea.style.height = newHeight + 'px';

        if (scrollHeight > maxHeight) {
          textarea.style.overflowY = 'auto';
        } else {
          textarea.style.overflowY = 'hidden';
        }
      }
    }
  }, [value, maxHeight]);

  return (
    <textarea
      disabled={disabled}
      ref={textareaRef}
      value={value}
      onKeyDown={onKeyDown}
      onChange={(e) => {
        const newValue = e.target.value;
        onChange(newValue);
      }}
      placeholder={placeholder}
      className="auto-resize-textarea"
      style={{ maxHeight: `${maxHeight}px` }}
    />
  );
}
